import { Conta } from '../types/financeiro';

export interface ContaExtendedMeta {
  numero_nf?: string;
  nf_url?: string;
  nf_nome?: string;
  boleto_url?: string;
  boleto_nome?: string;
  comprovante_url?: string;
  comprovante_nome?: string;
  outros_anexos?: Array<{ tipo: string; nome: string; url: string }>;
  valor_pago?: number;
  juros_multa?: number;
  desconto?: number;
  banco_id?: number;
  banco_nome?: string;
  cnpj_faturador?: string;
  empresa_faturadora?: string;
  observacao_pura?: string;
}

// Known bank keywords for heuristic detection
const KNOWN_BANKS: Array<{ id: number; name: string; aliases: RegExp }> = [
  { id: 5, name: 'Banco do Brasil', aliases: /\b(banco do brasil|bb|b\. brasil|banco brasil)\b/i },
  { id: 4, name: 'BTG Pactual', aliases: /\b(btg pactual|btg|banco btg)\b/i },
  { id: 1, name: 'Bradesco', aliases: /\b(bradesco|banco bradesco)\b/i },
  { id: 3, name: 'Santander', aliases: /\b(santander|banco santander)\b/i },
  { id: 6, name: 'Banco Cora', aliases: /\b(cora|banco cora)\b/i },
  { id: 7, name: 'Itaú', aliases: /\b(itau|itaú|banco itau|banco itaú)\b/i },
  { id: 8, name: 'Caixa Econômica', aliases: /\b(caixa|cef|caixa economica|caixa econômica)\b/i },
  { id: 9, name: 'Inter', aliases: /\b(banco inter|inter)\b/i },
  { id: 10, name: 'Nubank', aliases: /\b(nu pagamentos|nubank|nu)\b/i },
  { id: 11, name: 'Sicredi', aliases: /\b(sicredi)\b/i },
  { id: 12, name: 'Sicoob', aliases: /\b(sicoob)\b/i },
  { id: 13, name: 'Safra', aliases: /\b(safra|banco safra)\b/i },
];

/**
 * Parses the `obs` field to extract structured metadata (NF, Boleto, Comprovante, Valor Pago, Banco, CNPJ Faturador, etc.)
 */
export function parseContaObs(obs?: string | null, fallbackText?: string | null): ContaExtendedMeta {
  if (!obs && !fallbackText) return {};

  const meta: ContaExtendedMeta = {};
  const lines = (obs || '').split('\n');
  const cleanLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Check for NF pattern (Number)
    const nfMatch = trimmed.match(/^(?:NF|Nota Fiscal|NFe|Doc|Documento):\s*(.+)$/i);
    if (nfMatch) {
      meta.numero_nf = nfMatch[1].trim();
      continue;
    }

    // Check for NF File URL
    const nfUrlMatch = trimmed.match(/^(?:NF Arquivo|NF URL|Nota Fiscal Arquivo|Arquivo NF):\s*(.+)$/i);
    if (nfUrlMatch) {
      meta.nf_url = nfUrlMatch[1].trim();
      continue;
    }

    // Check for NF File Name
    const nfNomeMatch = trimmed.match(/^(?:NF Nome|Nota Fiscal Nome):\s*(.+)$/i);
    if (nfNomeMatch) {
      meta.nf_nome = nfNomeMatch[1].trim();
      continue;
    }

    // Check for Boleto URL
    const boletoUrlMatch = trimmed.match(/^(?:Boleto|Boleto URL|Arquivo Boleto):\s*(.+)$/i);
    if (boletoUrlMatch) {
      meta.boleto_url = boletoUrlMatch[1].trim();
      continue;
    }

    // Check for Boleto Nome
    const boletoNomeMatch = trimmed.match(/^(?:Boleto Nome):\s*(.+)$/i);
    if (boletoNomeMatch) {
      meta.boleto_nome = boletoNomeMatch[1].trim();
      continue;
    }

    // Check for Comprovante pattern
    const compMatch = trimmed.match(/^(?:Comprovante|Anexo|Arquivo):\s*(.+)$/i);
    if (compMatch) {
      meta.comprovante_url = compMatch[1].trim();
      continue;
    }

    // Check for Comprovante Nome
    const compNomeMatch = trimmed.match(/^(?:Nome Arquivo|Comprovante Nome):\s*(.+)$/i);
    if (compNomeMatch) {
      meta.comprovante_nome = compNomeMatch[1].trim();
      continue;
    }

    // Check for Valor Pago
    const vpMatch = trimmed.match(/^(?:Valor Pago|Pago R\$):\s*(.+)$/i);
    if (vpMatch) {
      const val = parseFloat(vpMatch[1].replace(/[^\d.,]/g, '').replace(',', '.'));
      if (!isNaN(val)) meta.valor_pago = val;
      continue;
    }

    // Check for Banco Name
    const bancoMatch = trimmed.match(/^(?:Banco|Instituicao Bancaria|Instituição Bancária|Conta Bancaria|Conta Bancária):\s*(.+)$/i);
    if (bancoMatch) {
      meta.banco_nome = bancoMatch[1].trim();
      continue;
    }

    // Check for Banco ID
    const bancoIdMatch = trimmed.match(/^(?:Banco ID|banco_id):\s*(\d+)$/i);
    if (bancoIdMatch) {
      meta.banco_id = parseInt(bancoIdMatch[1], 10);
      continue;
    }

    // Check for CNPJ Faturador
    const cnpjMatch = trimmed.match(/^(?:CNPJ Faturador|CNPJ Faturamento|Faturado por CNPJ|CNPJ Emissor|Empresa CNPJ):\s*(.+)$/i);
    if (cnpjMatch) {
      meta.cnpj_faturador = cnpjMatch[1].trim();
      continue;
    }

    // Check for Empresa Faturadora
    const empresaMatch = trimmed.match(/^(?:Empresa Faturadora|Empresa Faturamento|Faturado por Empresa|Unidade Faturadora|Filial):\s*(.+)$/i);
    if (empresaMatch) {
      meta.empresa_faturadora = empresaMatch[1].trim();
      continue;
    }

    cleanLines.push(line);
  }

  // Heuristic detection for bank if not explicitly defined
  if (!meta.banco_nome) {
    const combinedText = `${obs || ''} ${fallbackText || ''}`.toLowerCase();
    for (const kb of KNOWN_BANKS) {
      if (kb.aliases.test(combinedText)) {
        meta.banco_nome = kb.name;
        if (!meta.banco_id) meta.banco_id = kb.id;
        break;
      }
    }
  }

  meta.observacao_pura = cleanLines.join('\n').trim();
  return meta;
}

/**
 * Builds a structured `obs` string preserving NF, Boleto, Comprovante, Banco, CNPJ and Pure Notes
 */
export function buildContaObs(
  observacaoPura?: string,
  extra?: {
    numero_nf?: string;
    nf_url?: string;
    nf_nome?: string;
    boleto_url?: string;
    boleto_nome?: string;
    comprovante_url?: string;
    comprovante_nome?: string;
    valor_pago?: number;
    banco_id?: number;
    banco_nome?: string;
    cnpj_faturador?: string;
    empresa_faturadora?: string;
  }
): string {
  const parts: string[] = [];

  if (extra?.numero_nf && extra.numero_nf.trim()) {
    parts.push(`NF: ${extra.numero_nf.trim()}`);
  }

  if (extra?.nf_nome && extra.nf_nome.trim()) {
    parts.push(`NF Nome: ${extra.nf_nome.trim()}`);
  }

  if (extra?.nf_url && extra.nf_url.trim()) {
    parts.push(`NF Arquivo: ${extra.nf_url.trim()}`);
  }

  if (extra?.boleto_nome && extra.boleto_nome.trim()) {
    parts.push(`Boleto Nome: ${extra.boleto_nome.trim()}`);
  }

  if (extra?.boleto_url && extra.boleto_url.trim()) {
    parts.push(`Boleto: ${extra.boleto_url.trim()}`);
  }

  if (extra?.cnpj_faturador && extra.cnpj_faturador.trim()) {
    parts.push(`CNPJ Faturador: ${extra.cnpj_faturador.trim()}`);
  }

  if (extra?.empresa_faturadora && extra.empresa_faturadora.trim()) {
    parts.push(`Empresa Faturadora: ${extra.empresa_faturadora.trim()}`);
  }

  if (extra?.banco_nome && extra.banco_nome.trim()) {
    parts.push(`Banco: ${extra.banco_nome.trim()}`);
  }

  if (extra?.banco_id) {
    parts.push(`Banco ID: ${extra.banco_id}`);
  }

  if (extra?.valor_pago !== undefined && extra.valor_pago !== null) {
    parts.push(`Valor Pago: R$ ${extra.valor_pago.toFixed(2)}`);
  }

  if (extra?.comprovante_nome && extra.comprovante_nome.trim()) {
    parts.push(`Comprovante Nome: ${extra.comprovante_nome.trim()}`);
  }

  if (extra?.comprovante_url && extra.comprovante_url.trim()) {
    parts.push(`Comprovante: ${extra.comprovante_url.trim()}`);
  }

  if (observacaoPura && observacaoPura.trim()) {
    parts.push(observacaoPura.trim());
  }

  return parts.join('\n');
}

/**
 * Enriches a Conta object with extracted metadata
 */
export function enrichConta(c: any): Conta {
  const parsed = parseContaObs(c.obs, `${c.descricao || ''}`);
  const dbAnexos = Array.isArray(c.conta_anexo) ? c.conta_anexo : (Array.isArray(c.anexos) ? c.anexos : []);
  const legacyCount = [
    c.comprovante_url || parsed.comprovante_url,
    c.boleto_url || parsed.boleto_url,
    c.nf_url || parsed.nf_url
  ].filter(Boolean).length;

  const totalAnexosCount = dbAnexos.length > 0 ? dbAnexos.length : legacyCount;

  return {
    ...c,
    numero_nf: c.numero_nf || parsed.numero_nf || null,
    comprovante_url: c.comprovante_url || parsed.comprovante_url || null,
    comprovante_nome: c.comprovante_nome || parsed.comprovante_nome || null,
    boleto_url: c.boleto_url || parsed.boleto_url || null,
    boleto_nome: c.boleto_nome || parsed.boleto_nome || null,
    nf_url: c.nf_url || parsed.nf_url || null,
    nf_nome: c.nf_nome || parsed.nf_nome || null,
    anexos_count: totalAnexosCount,
    anexos: dbAnexos.length > 0 ? dbAnexos : undefined,
    valor_pago: c.valor_pago || parsed.valor_pago || (c.dt_pagamento ? c.valor : null),
    banco_id: c.banco_id || parsed.banco_id || null,
    banco_nome: c.banco_nome || parsed.banco_nome || null,
    cnpj_faturador: c.cnpj_faturador || parsed.cnpj_faturador || null,
    empresa_faturadora: c.empresa_faturadora || parsed.empresa_faturadora || null,
  };
}
